import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { clone } from "lodash";
import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/Modal";
import * as AccountStore from "./AccountStore";
import Avatar from "react-avatar";
import ApiKeyManager from "../apiKeys/ApiKeyManager";

const { Component } = React;

function TicksToDate(ticks) {
    var epoch = 621355968000000000; // ticks up to Jan 1 1970  // the date in ticks
    var date = ticks - epoch; // rebase to Jan 1 1970
    var millisec = date / 10000; // convert to milliseconds
    return new Date(millisec); // create a Javascript Date object
}

const ProfileSecurity = ({}) => {
    const user = AccountStore.useUser();
    const [deviceFingerprint, setDeviceFingerprint] = React.useState(AccountStore.Fingerprint());
    const [credentials, setCredentials] = React.useState([]);
    const [credentialOptions, setCredentialOptions] = React.useState({ excludeCredentials: [] } as PublicKeyCredentialCreationOptions);
    const [error, setError] = React.useState("");

    const [allowCredentials, setAllowCredentials] = React.useState(false);
    const [progress, setProgress] = React.useState(0);

    const [loaded, setLoaded] = React.useState(false);

    React.useEffect(() => {
        if (!loaded) {
            setError("");
            setLoaded(true);
            AccountStore.GetCredentials().then((creds) => {
                setCredentials(creds);
                if (navigator.credentials) {
                    AccountStore.FetchMakeCredentialOptions(deviceFingerprint).then((options) => {
                        setCredentialOptions(options);
                        setProgress(1);
                        if (options.rp.id == location.hostname) {
                            setProgress(2);
                            setAllowCredentials(true);
                        } else {
                            setError("URL is not secure");
                        }
                    });
                } else {
                    setError("This device does not support secure credentials");
                }
            });
        }
    }, [loaded]);

    const makeCredentials = async () => {
        if (credentials.filter((c) => c.deviceID == deviceFingerprint).length == 0) {
            let _options = clone(credentialOptions);

            const challengeKey = _options.user.id;
            _options.challenge = AccountStore.CoerceToArrayBuffer(_options.challenge);
            _options.user.id = AccountStore.CoerceToArrayBuffer(_options.user.id);
            if (_options.authenticatorSelection.authenticatorAttachment === null) {
                _options.authenticatorSelection.authenticatorAttachment = undefined;
            }
            _options.excludeCredentials.forEach((c) => {
                c.id = AccountStore.CoerceToArrayBuffer(c.id);
            });
            let newCredential;
            try {
                newCredential = await navigator.credentials.create({ publicKey: _options });
            } catch (e) {
                setError(e.toString());
                return;
            }
            setProgress(3);
            try {
                await registerNewCredential(newCredential, challengeKey);
            } catch (e) {
                setError(e.toString());
                return;
            }
            setProgress(4);
            AccountStore.GetCredentials().then((creds) => {
                setCredentials(creds);
            });
        } else {
            setError("This device already has credentials. Delete this device's credentials to continue");
        }
    };

    // This should be used to verify the auth data with the server
    const registerNewCredential = async (newCredential, challengeKey) => {
        let attestationObject = new Uint8Array(newCredential.response.attestationObject);
        let clientDataJSON = new Uint8Array(newCredential.response.clientDataJSON);
        let rawId = new Uint8Array(newCredential.rawId);

        const data = {
            id: newCredential.id,
            rawId: AccountStore.CoerceToBase64Url(rawId),
            type: newCredential.type,
            extensions: newCredential.getClientExtensionResults(),
            response: {
                attestationObject: AccountStore.CoerceToBase64Url(attestationObject),
                clientDataJSON: AccountStore.CoerceToBase64Url(clientDataJSON),
            },
        };

        let response;
        try {
            response = await AccountStore.RegisterCredentialWithServer(data, challengeKey);
        } catch (e) {
            setError(e.toString());
            return;
        }

        if (response.status !== "ok") {
            setError(response.errorMessage);
            return;
        }
    };

    const [currentUser, setCurrentUser] = React.useState(new AccountStore.Account({}));
    const [secureLoginState, setSecureLoginState] = React.useState("");

    React.useEffect(() => {
        AccountStore.GetCurrentLoggedInUser(true).then((user) => {
            setCurrentUser(user);
            let timestamp = TicksToDate(user.secureTimestamp());
            if (timestamp) {
                setSecureLoginState(+new Date(timestamp) > +new Date() - 1000 * 60 * 2 ? "verified" : "stale");
            } else {
                setSecureLoginState("unverified");
            }
        });
    }, []);

    const deleteCredential = (credentialID) => () => {
        AccountStore.DeleteCredential(credentialID).then(() => {
            setLoaded(false);
        });
    };

    const RenderSecureLoginState = () => {
        switch (secureLoginState) {
            case "verified":
                return (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        <FontAwesomeIcon icon={["fad", "check-circle"]} className="mr-1" />
                        Verified
                    </div>
                );
            case "stale":
                return (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                        <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="mr-1" />
                        Stale
                    </div>
                );
            case "unverified":
                return (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                        <FontAwesomeIcon icon={["fad", "question-circle"]} className="mr-1" />
                        Unverified
                    </div>
                );
            default:
                return (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                        Unknown
                    </div>
                );
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <div className="flex items-center gap-4">
                    <Avatar round={true} size="70" name={currentUser.fullName} src={currentUser.photo} />
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            {currentUser.firstName} {currentUser.lastName}
                        </h1>
                        <h2 className="text-xl text-gray-600">{currentUser.email}</h2>
                        <p className="text-sm text-gray-500">{currentUser.userName}</p>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                <nav className="flex space-x-4 px-6 py-3 border-b border-gray-200">
                    <Link
                        to="/profile"
                        className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    >
                        <FontAwesomeIcon icon={["fad", "user"]} className="mr-2" />
                        Profile
                    </Link>
                    <Link
                        to="/profile/data"
                        className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    >
                        <FontAwesomeIcon icon={["fad", "database"]} className="mr-2" />
                        Data
                    </Link>
                    <Link
                        to="/profile/mfa"
                        className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    >
                        <FontAwesomeIcon icon={["fad", "shield-check"]} className="mr-2" />
                        MFA
                    </Link>
                    <Link
                        to="/profile/security"
                        className="px-3 py-2 text-sm font-medium rounded-md text-blue-600 bg-blue-50 border-b-2 border-blue-600"
                    >
                        <FontAwesomeIcon icon={["fad", "lock"]} className="mr-2" />
                        Security
                    </Link>
                    <Link
                        to="/profile/notifications"
                        className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    >
                        <FontAwesomeIcon icon={["fad", "bell"]} className="mr-2" />
                        Notifications
                    </Link>
                </nav>
            </div>

            <ApiKeyManager scope="user" subjectId={user.id} />
            {/* Security Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                        <FontAwesomeIcon icon={["fad", "fingerprint"]} className="mr-2 text-purple-500" />
                        Passwordless Credentials
                    </h2>
                    {RenderSecureLoginState()}
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                    <div className="flex h-8 rounded-lg overflow-hidden border border-gray-200">
                        <div
                            className={`flex items-center justify-center text-xs font-medium text-white transition-all ${
                                progress >= 1 ? "bg-yellow-500" : "bg-gray-200"
                            }`}
                            style={{ width: "20%" }}
                        >
                            Device Secure
                        </div>
                        <div
                            className={`flex items-center justify-center text-xs font-medium text-white transition-all ${
                                progress >= 2 ? "bg-blue-500" : "bg-gray-200"
                            }`}
                            style={{ width: "20%" }}
                        >
                            Server Secure
                        </div>
                        <div
                            className={`flex items-center justify-center text-xs font-medium text-white transition-all ${
                                progress >= 3 ? "bg-indigo-600" : "bg-gray-200"
                            }`}
                            style={{ width: "20%" }}
                        >
                            Created Credential
                        </div>
                        <div
                            className={`flex items-center justify-center text-xs font-medium text-white transition-all ${
                                progress >= 4 ? "bg-green-500" : "bg-gray-200"
                            }`}
                            style={{ width: "40%" }}
                        >
                            Server Saved Credential
                        </div>
                        {error && (
                            <div className="flex items-center justify-center text-xs font-medium text-white bg-red-500" style={{ width: `${100 - progress * 20}%` }}>
                                Error
                            </div>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <div className="flex">
                            <FontAwesomeIcon icon={["fad", "exclamation-circle"]} className="text-red-500 mr-2" />
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                )}

                {allowCredentials && (
                    <div className="flex gap-3 mb-6">
                        <Button variant="primary" onClick={makeCredentials}>
                            <FontAwesomeIcon icon={["fad", "key"]} className="mr-2" />
                            Setup Credentials
                        </Button>
                        <Button
                            variant="outline-secondary"
                            onClick={() => {
                                window.location.assign("/account/securelogin?returnURL=/");
                            }}
                        >
                            <FontAwesomeIcon icon={["fad", "vial"]} className="mr-2" />
                            Test Credentials
                        </Button>
                    </div>
                )}

                {/* Credentials Table */}
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credential</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {credentials.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center">
                                        <FontAwesomeIcon icon={["fad", "key-skeleton"]} className="text-4xl text-gray-300 mb-3" />
                                        <p className="text-gray-500">No credentials found</p>
                                    </td>
                                </tr>
                            ) : (
                                credentials.map((c) => (
                                    <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <code className="text-xs text-gray-900 bg-gray-100 px-2 py-1 rounded">{c.id}</code>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <code className="text-xs text-gray-900 bg-gray-100 px-2 py-1 rounded">{c.deviceID}</code>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {c.deviceID == deviceFingerprint && (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    <FontAwesomeIcon icon={["fad", "check"]} className="mr-1" />
                                                    This Device
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <Button variant="outline-danger" size="sm" onClick={deleteCredential(c.id)}>
                                                <FontAwesomeIcon icon={["fad", "trash"]} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
export default ProfileSecurity;
